import { useEffect, useRef, useState } from "react";
import api from "../lib/api";

const RECEIPT_ID = "f9a1600f788a522d15acceef7bb7e6bbb7430b14a133ae58fd3946ce25371155:externally_terminated_source_aborted:v1";
const DISPOSITION = "externally_terminated_source_aborted";
const ROOT = "/admin/operator-automation/revcomps/incident-b60";

const INTENT_ID = "f9a1600f788a522d15acceef7bb7e6bbb7430b14a133ae58fd3946ce25371155";
const RUN_ID = "b60b3c6d-fbc9-431f-accb-88beee7bb5a1";
const isRecord = (value) => value != null && typeof value === "object" && !Array.isArray(value);
const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value, key);

// This is an observation of the fixed disposition, never a server receipt or
// a grant. The separate authenticated Scan endpoint revalidates the full journal,
// immutable receipt, actor and exact current settings before acquiring a lease.
function receiptFromSettings(settings) {
  if (!isRecord(settings) || settings.operator_key !== "revcomps"
      || settings.control_plane_available !== true || settings.settings_present !== true
      || settings.settings_generation !== 11 || settings.schedule_generation !== 9
      || hasOwn(settings, "rev_source_manifest_intent") || hasOwn(settings, "rev_checkpoint_workflow")) return null;
  const marker = settings.rev_writer_intent;
  const lease = settings.execution_lease;
  if (!isRecord(marker) || !isRecord(lease)
      || Object.keys(marker).sort().join(",") !== [
        "checkpoint_id", "cleanup_confirmed", "disposition_receipt_hash", "disposition_receipt_id",
        "intent_id", "pending", "phase", "run_id", "state", "version",
      ].join(",")
      || marker.intent_id !== INTENT_ID || marker.run_id !== RUN_ID
      || marker.state !== DISPOSITION || marker.pending !== false || marker.cleanup_confirmed !== false
      || marker.checkpoint_id !== null || marker.phase !== "legacy"
      || marker.version !== "rev_create_only_checkpoint_v1" || marker.disposition_receipt_id !== RECEIPT_ID
      || typeof marker.disposition_receipt_hash !== "string" || !/^[0-9a-f]{64}$/.test(marker.disposition_receipt_hash)
      || lease.operator_key !== "revcomps" || lease.run_id !== RUN_ID || lease.active !== false
      || lease.settings_generation !== 11 || lease.schedule_generation !== 9
      || lease.execution_type !== "scheduled" || lease.completion_status !== DISPOSITION
      || lease.recovery_reason !== "exact_b60_external_source_abort"
      || typeof lease.completed_at !== "string" || !Number.isFinite(Date.parse(lease.completed_at))
      || lease.completed_at !== lease.released_at
      || lease.source_abort_receipt_hash !== marker.disposition_receipt_hash) return null;
  return { receipt_id: RECEIPT_ID, receipt_hash: marker.disposition_receipt_hash };
}

function hasDispositionObservation(settings) {
  return Boolean(settings?.rev_writer_intent?.disposition_receipt_id
    || settings?.rev_writer_intent?.disposition_receipt_hash
    || settings?.rev_writer_intent?.state === DISPOSITION
    || settings?.execution_lease?.source_abort_receipt_hash
    || settings?.execution_lease?.completion_status === DISPOSITION);
}

export default function RevSourceAbortRecovery({ settings, busy, setBusy, scanEnabled, onDisposition, onScanResult }) {
  const [confirmed, setConfirmed] = useState(false);
  const [scanConfirmed, setScanConfirmed] = useState(null);
  const [localReceipt, setLocalReceipt] = useState(null);
  const durableReceipt = receiptFromSettings(settings);
  const receipt = durableReceipt || (localReceipt && localReceipt.settings === settings ? localReceipt.data : null);
  const dispositionObserved = receipt != null || hasDispositionObservation(settings);
  const scanConfirmationCurrent = receipt != null && scanConfirmed?.receipt_hash === receipt.receipt_hash
    && scanConfirmed?.settings === settings;
  const [message, setMessage] = useState("");
  const [held, setHeld] = useState(false);
  const [scanSubmitted, setScanSubmitted] = useState(false);
  const submission = useRef(false);
  const attempted = useRef(new Set());
  const active = useRef(true);
  useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
  useEffect(() => { setScanConfirmed(null); }, [settings]);

  const run = async (phase) => {
    if (submission.current || attempted.current.has(phase) || busy != null || held
        || (phase === "source-abort" ? !confirmed || dispositionObserved : !receipt || !scanConfirmationCurrent || !scanEnabled || scanSubmitted)) return;
    submission.current = true;
    attempted.current.add(phase);
    setBusy(`rev-b60-${phase}`);
    setMessage("");
    if (phase === "fresh-source-scan") setScanSubmitted(true);
    try {
      // The authenticated API owns the fixed incident, evidence and limits.
      // Never supply a run identity, external death assertion or phase token.
      const { data } = await api.post(`${ROOT}/${phase}`, { confirm: true });
      if (!active.current) return;
      if (phase === "source-abort") {
        if (!["reconciled", "already_reconciled"].includes(data?.status)
            || data.disposition !== DISPOSITION || data.receipt_id !== RECEIPT_ID
            || !/^[0-9a-f]{64}$/.test(data.receipt_hash)
            || data.cleanup_confirmed !== false || data.new_scan_started !== false) {
          throw new Error("unconfirmed_disposition");
        }
        setLocalReceipt({ data, settings });
        setConfirmed(false);
        setMessage("Source-only termination recorded. The old read's remote outcome remains unknown. No competitions were published.");
        onDisposition?.(data);
      } else {
        if (data?.operator_key !== "revcomps" || data.phase !== "scan" || data.dry_run !== true) {
          throw new Error("unconfirmed_scan");
        }
        setMessage(data.status === "ok"
          ? "Fresh source scan returned. Review its completeness and evidence before any further action."
          : "Fresh source scan is blocked or incomplete. Review this exact run before continuing.");
        onScanResult?.(data);
      }
    } catch (error) {
      if (!active.current) return;
      setHeld(true);
      const status = error?.response?.status;
      setMessage(status === 401 || status === 403
        ? "Admin authorisation was refused. Sign in normally and reconcile this exact operation before continuing."
        : status === 404
        ? "This recovery endpoint is unavailable. Confirm the reviewed API deployment before continuing."
        : status === 409
        ? "The server refused recovery because the exact incident evidence or state does not match. Review that refusal; do not replay the old run."
        : "The operation outcome is unconfirmed. Read back this exact incident or run before another request. Do not retry for proof.");
    } finally {
      if (active.current) setBusy(null);
      submission.current = false;
    }
  };

  return (
    <section className="w-full rounded border border-amber-400/20 p-3 text-xs space-y-2" data-testid="rev-b60-recovery">
      <p>Recover the interrupted Rev source read (b60). The server checks the fixed evidence and generations. The old read's remote outcome and cleanup remain unconfirmed.</p>
      <label className="block">
        <input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)}
          disabled={busy != null || held || dispositionObserved} data-testid="rev-b60-confirm" />
        Record this source-only termination. This does not resume the old run or authorise Apply, Verify or Publish.
      </label>
      <button type="button" onClick={() => run("source-abort")}
        disabled={busy != null || held || !confirmed || dispositionObserved}
        className="px-3 py-2 rounded bg-amber-500/20 disabled:opacity-45" data-testid="rev-b60-disposition">
        Record source-only termination
      </button>
      {receipt && <p data-testid="rev-b60-receipt">Receipt: {receipt.receipt_id}. Evidence: {receipt.receipt_hash}.</p>}
      <label className="block">
        <input type="checkbox" checked={scanConfirmationCurrent} onChange={(event) => setScanConfirmed(event.target.checked
          ? { receipt_hash: receipt?.receipt_hash, settings } : null)}
          disabled={busy != null || held || !receipt || scanSubmitted || !scanEnabled} data-testid="rev-b60-scan-confirm" />
        Run one fresh guarded source scan using the existing limits. Check the returned evidence separately.
      </label>
      <button type="button" onClick={() => run("fresh-source-scan")}
        disabled={busy != null || held || !receipt || !scanConfirmationCurrent || scanSubmitted || !scanEnabled}
        className="px-3 py-2 rounded bg-sky-500/20 disabled:opacity-45" data-testid="rev-b60-scan">
        Run one fresh source scan
      </button>
      {message && <p role="status" data-testid="rev-b60-status">{message}</p>}
    </section>
  );
}
